import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {Person} from '@microsoft/mgt-react';

import Utility from '../../../util/utilityfunctions';
import Env from '../../../util/env';
import {currentResource, labels} from '../../../util/utils';
import {engagementUserStatus, entities, reviewNoteRoutes, serviceURL} from '../../../util/uiconstants';

import {
	addNewNote,
	deleteNoteReply,
	deleteParentNote,
	replyToNote,
	updateNote
} from '../../../actions/NotesModal/rnhnotedetailaction';
import {setIsMediaPlaying} from '../../../actions/MediaPlaying/mediaPlayingAction';

import {getEngagementTeamMembers} from '../../../actions/Engagement/engagementactions';
import getPayload, {
	TYPE_ADD_NOTE,
	TYPE_ADD_REPLY,
	TYPE_SAVE_NOTE,
	TYPE_SAVE_REPLY,
	TYPE_STATUS_CHANGE
} from './getPayload';

import {NotesModal, ErrorSummaryOverride} from '@ey/canvascoreservices';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import {raiseJSValidationError} from '../../../actions/erroractions';
import StyledNotesModalWrapper from './StyledNotesModalWrapper';

const NotesModalWrapper = ({
	show,
	entityId,
	onClose,
	renderDetails,
	entityTypeId,
	externalLoading,
	isDashboard,
	instructionalText,
	handleGetNotes,
	headerLabel,
	disableAdd,
	disableFilters,
	viewKey,
	bodyId
}) => {
	const [
		userPreferences,
		engagementTeamMembers,
		engagementId,
		isVoiceNoteRecordingEnabled,
		rnhNoteDetail
	] = useSelector((store) => [
		store.userPreferences,
		store.engagementTeamMembers,
		store.engagement.engagementId,
		Utility.isVoiceNoteRecordingEnabled(store),
		store.rnhNoteDetail
	]);

	const isMediaPlaying = useSelector(({isMediaPlaying}) => isMediaPlaying);

	const dispatch = useDispatch();

	// engagementTeamMembers useEffect
	useEffect(() => {
		if (engagementTeamMembers.length === 0) {
			const requestsurl = Utility.getEngagementUsersUrl(engagementId, [
				engagementUserStatus.Active,
				engagementUserStatus.Pending,
				engagementUserStatus.DeActivated,
				engagementUserStatus.ScheduledInactive
			]);
			dispatch(getEngagementTeamMembers(requestsurl, true));
		}
	}, [engagementTeamMembers]);

	const noteModalLabels = {
		closeButton: labels.close,
		closed: labels.closedStatus,
		open: labels.openStatus,
		cleared: labels.clearedStatus,
		header: headerLabel,
		allNotes: currentResource.reviewNoteLabels.allNotesLabel,
		myNotes: currentResource.reviewNoteLabels.myNotesLabel,
		assignedToMe: labels.assignedToMe,
		noNotesFound: currentResource?.reviewNoteModalLabels?.noNotesFound,
		noNotesFoundInstructional: currentResource?.reviewNoteModalLabels?.noNotesAssociatedWithDocumentLabel,
		//EDITOR
		addNoteButtonLabel: currentResource?.reviewNoteModalLabels?.addNewReviewNoteButtonText,
		saveNoteButtonLabel: labels.saveLabel,
		messagePlaceholder: currentResource.reviewNoteLabels.addNewNoteModalTitleLabel,
		messagePlaceholderReply: currentResource?.reviewNoteModalLabels?.replyInputPlaceholder,
		messagePlaceholderEdit: currentResource?.reviewNoteModalLabels?.noteInputPlaceholderEdit,
		messagePlaceholderReplyEdit: currentResource?.reviewNoteModalLabels?.replyInputPlaceholderEdit,
		addReplyButtonLabel: currentResource?.reviewNoteModalLabels?.reply,
		characters: labels.characters,
		of: labels.of,
		formRequired: currentResource?.reviewNoteModalLabels?.required,
		formMaxLength: currentResource.reviewNoteLabels.formMaxLength,
		toFieldLabel: labels.assignedTo,
		priorityFieldLabel: currentResource.reviewNoteLabels.priorityLabel,
		dueDateFieldLabel: labels.dueDate,
		voiceNoteButtonLabel: currentResource.reviewNoteLabels.voiceNoteButtonLabel,
		stopRecordingButtonLabel: currentResource.reviewNoteLabels.stopRecordingButtonLabel,
		cancelButtonLabel: labels.cancelButtonLabel,
		noResultsFound: labels.noResultsFound,
		playButtonTitle: labels.playButtonTitle,
		pauseButtonTitle: labels.stopRecordingButtonLabel,
		microphoneBlockedMessage: currentResource.reviewNoteLabels.microphoneBlockedMessage,
		microphoneBlockedOnVideoMessage: currentResource.reviewNoteLabels.microphoneBlockedOnVideoMessage,
		notInMainWindowVoice: currentResource.reviewNoteLabels.notInMainWindowVoice,
		notInMainWindowScreen: currentResource.reviewNoteLabels.notInMainWindowScreen,
		voiceNoteNotAvailable: currentResource.reviewNoteLabels.voiceNoteNotAvailable,
		screenRecord: currentResource.reviewNoteLabels.screenRecord,
		playbackReview: labels.playbackReview,
		//NOTE
		assignedTo: labels.assignedTo,
		dueDate: labels.dueDate,
		edit: labels.edit,
		delete: labels.delete,
		clear: labels.clear,
		reopen: currentResource.reviewNoteLabels.reopen,
		close: labels.close,
		reply: labels.reply,
		confirm: labels.confirm,
		cancel: labels.cancelButtonLabel,
		deleteModalHeaderNote: currentResource?.reviewNoteModalLabels?.deleteIconModalTitleLabel,
		deleteModalHeaderReply: currentResource?.reviewNoteModalLabels?.deleteReplyIconModalTitle,
		deleteModalTextNote: currentResource?.reviewNoteModalLabels?.deleteIconModalConfirmMessageParent,
		deleteModalTextReply: currentResource?.reviewNoteModalLabels?.deleteIconModalConfirmMessage,
		showMore: currentResource?.reviewNoteModalLabels?.showMore,
		showLess: currentResource?.reviewNoteModalLabels?.showLess,
		view: labels.view,
		notSelected: labels.noOptionSelected
	};

	const requestsUrl = Env.getBaseURL(serviceURL, engagementId) + reviewNoteRoutes.annotation;

	const getPayloadConfig = {
		userPreferences,
		isTask: entityTypeId === entities.Task,
		entityId,
		engagementId,
		entityTypeId,
		viewKey,
		bodyId
	};

	const handleOnNoteAdd = (data) => {
		const {noteData, noteInfo} = getPayload(data, getPayloadConfig, TYPE_ADD_NOTE);
		return dispatch(addNewNote(requestsUrl, noteData, noteInfo))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const handleOnReplyAdd = (data, parentId) => {
		const {noteData} = getPayload(data, {...getPayloadConfig, parentId}, TYPE_ADD_REPLY);
		return dispatch(replyToNote(requestsUrl, noteData))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const handleOnNoteSave = (data, otherData) => {
		const {noteData} = getPayload(data, {...getPayloadConfig, ids: [otherData.id]}, TYPE_SAVE_NOTE);
		return dispatch(updateNote(requestsUrl, noteData, otherData.id, userPreferences.id))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const handleOnReplySave = (data, otherData) => {
		const {noteData} = getPayload(data, {...getPayloadConfig, ids: [otherData.id]}, TYPE_SAVE_REPLY);
		return dispatch(updateNote(requestsUrl, noteData, otherData.id, userPreferences.id))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const handleOnStatusChange = (data, statusId, statusChangeUserName) => {
		const {noteData} = getPayload(data, {...getPayloadConfig, statusId, statusChangeUserName}, TYPE_STATUS_CHANGE);
		return dispatch(updateNote(requestsUrl, noteData, data?.id, userPreferences.id, false, noteData))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const handleOnNoteDelete = (data) => {
		const newData = {
			noteId: data.id,
			modifiedDate: new Date().toISOString()
		};
		return dispatch(deleteParentNote(`${requestsUrl}${data.id}`, newData))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const handleOnReplyDelete = (data) => {
		const newData = {
			noteId: data.id,
			modifiedDate: new Date().toISOString()
		};
		return dispatch(deleteNoteReply(`${requestsUrl}${data.id}`, newData))
			.then(() => false)
			.catch((e) => !!e?.message);
	};

	const renderToastNotification = () => (
		<ErrorSummaryOverride>
			<ErrorSummary />
		</ErrorSummaryOverride>
	);

	const props = {
		labels: noteModalLabels,
		show,
		onClose,
		notes: rnhNoteDetail,
		getNotes: handleGetNotes,
		onNoteAdd: handleOnNoteAdd,
		onReplyAdd: handleOnReplyAdd,
		onNoteSave: handleOnNoteSave,
		onReplySave: handleOnReplySave,
		onStatusChange: handleOnStatusChange,
		onNoteDelete: handleOnNoteDelete,
		onReplyDelete: handleOnReplyDelete,
		toastNotification: renderToastNotification,
		onToastNotification: (msg) => dispatch(raiseJSValidationError(msg)),
		userPreferences,
		engagementId,
		engagementTeamMembers,
		priorityResource: currentResource.PriorityType,
		noteStatusResource: currentResource.filterReviewNoteStatus,
		noteViewResource: currentResource.notesFilterLabels,
		documentServiceURL: Env.getDocServiceUrl(`documents.json/{documentId}?engagementid=${engagementId}`),
		isVoiceNoteRecordingEnabled,
		isMediaRecordingEnabled: isVoiceNoteRecordingEnabled,
		externalLoading,
		disableAdd,
		disableFilters,
		Person,
		isMediaPlaying,
		setIsMediaPlaying: (isRecording) => dispatch(setIsMediaPlaying(isRecording))
	};

	if (renderDetails) props.details = renderDetails;

	if (isDashboard) {
		props.disableAdd = true;
		props.disableFilters = true;
	}

	if (instructionalText) props.instructionalText = instructionalText;

	const isDarkMode = Utility.isDarkTheme();

	return (
		<StyledNotesModalWrapper isDarkMode={isDarkMode}>
			<NotesModal {...props} />
		</StyledNotesModalWrapper>
	);
};

NotesModalWrapper.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	entityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	renderDetails: PropTypes.func,
	entityTypeId: PropTypes.oneOf([entities.Document, entities.Task]),
	externalLoading: PropTypes.bool,
	isDashboard: PropTypes.bool,
	instructionalText: PropTypes.string,
	handleGetNotes: PropTypes.func.isRequired,
	headerLabel: PropTypes.string.isRequired
};

NotesModalWrapper.defaultProps = {
	renderDetails: null,
	entityTypeId: entities.Document,
	externalLoading: false,
	isDashboard: false,
	instructionalText: null
};

export default NotesModalWrapper;
